import { useState } from 'react';
import {
  ArrowRight,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Palette,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Pencil,
  Sparkles,
} from 'lucide-react';
import type { EnhancedFieldMapping } from '../../../types/mapping';
import { SOURCE_MODULES, MAPPABLE_PROPERTIES } from '../../../types/mapping';
import { useMappingStore } from '../../../stores/mappingStore';

interface MappingCardProps {
  mapping: EnhancedFieldMapping;
  elementId: string;
  elementType: 'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line';
  onEdit: (mapping: EnhancedFieldMapping) => void;
}

export default function MappingCard({
  mapping,
  elementId,
  elementType,
  onEdit,
}: MappingCardProps) {
  const { removeMapping, validateMapping, schema } = useMappingStore();
  const [showDetails, setShowDetails] = useState(false);

  const sourceConfig = SOURCE_MODULES[mapping.source];
  const propertyConfig = MAPPABLE_PROPERTIES.find(
    (p) => p.key === mapping.targetProperty
  );

  const validation = validateMapping(mapping, elementType);
  const SourceIcon = mapping.source === 'ad' ? Palette : GraduationCap;

  const isEav =
    mapping.filterColumn === 'attribute_key' &&
    !!mapping.filterValue &&
    mapping.columnName === 'attribute_value';

  const virtualField = isEav && schema
    ? schema.tables
        .find((t) => t.table_name === mapping.tableName)
        ?.virtual_columns?.find((v) => v.field_key === mapping.filterValue)
    : null;

  const virtualLabel = virtualField?.label ?? mapping.filterValue ?? null;
  const displayColumn = isEav && virtualLabel ? virtualLabel : mapping.columnName;

  const handleRemove = () => {
    removeMapping(elementId, mapping.id);
  };

  return (
    <div
      className={`rounded-lg border transition-colors ${
        validation.isValid
          ? 'bg-[#1a1a2e]/50 border-white/[0.06] hover:border-white/[0.1]'
          : 'bg-red-500/5 border-red-500/20 hover:border-red-500/30'
      }`}
    >
      <div className="flex items-center gap-2 p-2.5">
        <div className="shrink-0">
          {validation.isValid ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-red-400" />
          )}
        </div>

        <div
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
            mapping.source === 'ad'
              ? 'bg-pink-500/10 text-pink-400 border-pink-500/20'
              : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
          }`}
        >
          <SourceIcon className="w-2.5 h-2.5" />
          {sourceConfig.label.split(' ')[0]}
        </div>

        <span
          className={`flex items-center gap-1 text-xs truncate max-w-[100px] ${
            isEav ? 'text-cyan-300' : 'text-white/70'
          }`}
          title={
            isEav
              ? `${mapping.tableName} · virtual: ${virtualLabel}`
              : `${mapping.tableName}.${mapping.columnName}`
          }
        >
          {isEav && <Sparkles className="w-2.5 h-2.5 shrink-0" />}
          <span className="truncate">{displayColumn}</span>
        </span>

        <ArrowRight className="w-3 h-3 text-white/20 shrink-0" />

        <span
          className="text-xs text-purple-300 truncate max-w-[80px]"
          title={propertyConfig?.label || mapping.targetProperty}
        >
          {propertyConfig?.label || mapping.targetProperty}
        </span>

        <div className="flex-1" />

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors"
          title="Toggle details"
        >
          {showDetails ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={() => onEdit(mapping)}
          className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors"
          title="Edit mapping"
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          onClick={handleRemove}
          className="p-1 rounded hover:bg-red-500/10 text-white/30 hover:text-red-400 transition-colors"
          title="Remove mapping"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {showDetails && (
        <div className="px-2.5 pb-2.5 border-t border-white/[0.04] pt-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/30 w-14">Table:</span>
            <span className="text-[10px] text-white/50 font-mono">
              {mapping.tableName}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/30 w-14">Column:</span>
            <span className="text-[10px] text-white/50 font-mono">
              {mapping.columnName}
            </span>
            <span className="text-[10px] px-1 py-0.5 rounded bg-white/[0.04] text-white/30">
              {mapping.columnDataType}
            </span>
          </div>

          {isEav && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-cyan-400/70 w-14">Field:</span>
                <Sparkles className="w-2.5 h-2.5 text-cyan-400 shrink-0" />
                <span className="text-[10px] text-cyan-300 font-mono">
                  {virtualLabel}
                </span>
              </div>
              {mapping.filterCategory && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-cyan-400/70 w-14">Category:</span>
                  <span className="text-[10px] text-cyan-400/80">
                    {mapping.filterCategory}
                  </span>
                </div>
              )}
            </>
          )}

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/30 w-14">Fallback:</span>
            <span className="text-[10px] text-white/50 italic">
              {mapping.fallbackValue || '(none)'}
            </span>
          </div>

          {mapping.transformExpression && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-white/30 w-14">Transform:</span>
              <span className="text-[10px] text-purple-300 font-mono">
                {mapping.transformExpression}
              </span>
            </div>
          )}

          {!validation.isValid && validation.errors.length > 0 && (
            <div className="mt-1 space-y-1">
              {validation.errors.map((error, i) => (
                <div key={i} className="flex items-start gap-1.5 px-1.5 py-1 rounded bg-red-500/10">
                  <XCircle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-[10px] text-red-400">{error}</span>
                </div>
              ))}
            </div>
          )}

          {validation.warnings.length > 0 && (
            <div className="mt-1 space-y-1">
              {validation.warnings.map((warning, i) => (
                <div key={i} className="flex items-start gap-1.5 px-1.5 py-1 rounded bg-amber-500/10">
                  <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-[10px] text-amber-400">{warning}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
